import { Box, Button } from '@mantine/core'
import { useRouter } from 'next/router'
import { useState, useMemo } from 'react'

import { useAppDispatch } from '@flecto-io/core'
import { getBookingById } from '@flecto-io/core/src/actions'
import {
  useQueryGetAllBookingProducts,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import { Page } from '@flecto/ui/saas'

import { CardBookingDepositSummary } from '../../booking-payments'
import {
  CardBookingProductsPickedUpSummary,
  CardBookingProductsReservedSummary,
  CardBookingProductsReturnedSummary,
} from '../../booking-products'
import { TableBookingProductsReturn } from '../components/TableBookingProductsReturn/TableBookingProductsReturn'
import { useModalBookingReturnConfirmation } from '../hooks/useModalBookingReturnConfirmation'
import styles from './styles.module.css'

export function PageBookingProductsReturn() {
  const router = useRouter()
  const bookingId = String(router.query.id)
  const [bookingProductsToReturn, setBookingProductsToReturn] = useState<
    CoreBookingProduct[]
  >([])
  const { t } = useTranslation('saas', { keyPrefix: 'booking_pickup_return' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })

  const dispatch = useAppDispatch()

  const { data: booking, isLoading } = useQueryGetBookingById({ bookingId })

  const { data: bookingProducts, isLoading: isLoadingBookingProducts } =
    useQueryGetAllBookingProducts({ bookingId })

  const { openModal } = useModalBookingReturnConfirmation({
    bookingId,
    bookingProductsToReturn,
    onSuccess: () => {
      dispatch(getBookingById({ id: String(bookingId) }))
      navigationBack()
    },
  })

  const newTotalReturnProducts = useMemo(
    () =>
      bookingProductsToReturn.reduce(
        (acc, bi) =>
          acc +
          Number(bi.returned_quantity) +
          Number(bi.local_old_returned_quantity),
        0
      ),
    [bookingProductsToReturn]
  )

  const keptDepositCents = useMemo(
    () =>
      bookingProductsToReturn.reduce(
        (acc, bi) => acc + Number(bi.kept_deposit_cents),
        booking?.meta?.kept_deposit_cents ?? 0
      ),
    [bookingProductsToReturn]
  )

  const filteredBookingProducts = useMemo(() => {
    return bookingProducts.filter((bi) => bi.picked_up_quantity > 0)
  }, [bookingProducts])

  const navigationBack = () => {
    router.push(`/bookings/${bookingId}`)
  }

  return (
    <Page withContainer withLoading={isLoading}>
      <Page.Close onClick={navigationBack} />
      <Page.Title>{t('page_return_title')}</Page.Title>
      <Page.Actions>
        <Button onClick={openModal}>{tActions('apply')}</Button>
      </Page.Actions>
      <Page.Content>
        <Box className={styles.summaryRowReturn} mb='lg'>
          <CardBookingProductsReservedSummary bookingId={bookingId} />

          <CardBookingProductsPickedUpSummary bookingId={bookingId} />

          <CardBookingProductsReturnedSummary
            bookingId={bookingId}
            previewNewReturnTotal={newTotalReturnProducts}
          />

          <CardBookingDepositSummary
            cents={booking?.meta?.deposit_cents ?? 0}
            keptCents={keptDepositCents}
          />
        </Box>
        <Box mb='lg'>
          <TableBookingProductsReturn
            isLoading={isLoadingBookingProducts}
            bookingProducts={filteredBookingProducts}
            onSelectBookingProducts={setBookingProductsToReturn}
          />
        </Box>
      </Page.Content>
    </Page>
  )
}
